import React, { useRef } from 'react';
import { createContextHelper } from 'zdocs-web-core/lib/utils/context-utils';

export enum BrandType {
  ZILLOW = 'Z',
  ZILLOW_LEGACY = 'ZL',
  SHOWING_TIME_PLUS = 'STP',
}

export type BrandContextType = {
  type: BrandType;
  name: string;
  favicon: string;
  assetBasePath: string;
};

const getBrandFromUrl = () => {
  const queryParams = new URLSearchParams(window.location.search);
  const brandParam = queryParams.get('brand') as BrandType;
  const isValidBrand = Object.values(BrandType).includes(brandParam);
  return isValidBrand ? brandParam : BrandType.ZILLOW_LEGACY;
};

const getBrandName = (type: BrandType) => {
  if (type === BrandType.SHOWING_TIME_PLUS) {
    return 'ShowingTime+';
  }
  return 'Zillow';
};

const getFavIconFilename = (type: BrandType) => {
  if (type === BrandType.SHOWING_TIME_PLUS) {
    return 'favicon.png';
  }
  return 'favicon.ico';
};

const getAssetBasePath = (type: BrandType) => {
  const isZillow = type === BrandType.ZILLOW;
  const urlPathParts = window.location.pathname.split('/');
  const lastUrlPathPart = urlPathParts[urlPathParts.length - 1];
  const appBasePath = lastUrlPathPart === '' ? '' : `${lastUrlPathPart}/`;
  return `${appBasePath}${isZillow ? '../' : ''}`;
};

export const getBrandOptions = () => {
  const type = getBrandFromUrl();
  const name = getBrandName(type);
  const favicon = getFavIconFilename(type);
  const assetBasePath = getAssetBasePath(type);

  return { type, name, favicon, assetBasePath };
};

const setTitle = (name: string) => {
  document.title = `${name} Docs`;
};

const setFavicon = (assetBasePath: string, filename: string) => {
  let favicon = document.querySelector<HTMLLinkElement>('[rel*="icon"]');

  if (!favicon) {
    favicon = document.createElement('link');
    favicon.rel = 'shortcut icon';
    document.head.appendChild(favicon);
  }

  favicon.href = `${assetBasePath}${filename}`;
};

export const brandInit = (context: BrandContextType) => {
  const { name, assetBasePath, favicon } = context;

  setTitle(name);
  setFavicon(assetBasePath, favicon);
};

type BrandOptionsType = {
  favicon: string;
} & BrandContextType;

const useBrand = () => {
  const brandRef = useRef<BrandOptionsType>();

  if (!brandRef.current) {
    brandRef.current = getBrandOptions();
  }

  return brandRef.current;
};

export const [useBrandContext, BrandContext] =
  createContextHelper<BrandContextType>();

type Props = {
  children: React.ReactNode;
};

export default function BrandProvider({ children }: Props) {
  const brand = useBrand();

  return <BrandContext value={brand}>{children}</BrandContext>;
}
